import { useState } from 'react';
import type { AdminWeekStandingsDTO } from '@pickem/shared';
import { useApi } from '../../lib/useApi';
import { Card, Spinner, ErrorBanner, EmptyState } from '../../components/ui';
import { ordinal } from '../../lib/format';

/**
 * Admin "Results" tab: weekly and overall (season) standings for a league,
 * with a screen toggle and a branded print button that prints BOTH — weekly
 * on the first sheet, overall on the second. Names arrive already proper-cased
 * from the server (via fullName()).
 */
export function AdminWeekResults({ weekId }: { weekId: string }) {
  const { data, error, loading } = useApi<AdminWeekStandingsDTO>(
    `/admin/weeks/${weekId}/standings`,
  );
  const [view, setView] = useState<'weekly' | 'overall'>('weekly');

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} />;
  if (!data) return null;

  const hasResults = data.weekly.length > 0 || data.overall.length > 0;

  return (
    <Card>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex overflow-hidden rounded border border-line">
          <button
            className={`px-4 py-2 text-sm font-medium ${
              view === 'weekly' ? 'bg-grove text-white' : 'bg-surface text-muted'
            }`}
            onClick={() => setView('weekly')}
          >
            Weekly
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium ${
              view === 'overall' ? 'bg-grove text-white' : 'bg-surface text-muted'
            }`}
            onClick={() => setView('overall')}
          >
            Overall
          </button>
        </div>
        <button
          className="btn-outline"
          onClick={() => void printStandings(data)}
          disabled={!hasResults}
        >
          Print standings
        </button>
      </div>

      {!hasResults ? (
        <EmptyState title="No results yet">
          Standings appear once this week&rsquo;s results have been calculated.
        </EmptyState>
      ) : view === 'weekly' ? (
        <WeeklyTable data={data} />
      ) : (
        <OverallTable data={data} />
      )}
    </Card>
  );
}

function WeeklyTable({ data }: { data: AdminWeekStandingsDTO }) {
  return (
    <div className="overflow-x-auto">
      <h3 className="mb-2 font-display text-2xl uppercase text-ink">
        {data.league.name} — {data.week.displayName} standings
      </h3>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-line bg-cream/60 text-xs uppercase tracking-wide text-muted">
            <th className="px-3 py-2 text-center">Place</th>
            <th className="px-3 py-2 text-left">Player</th>
            <th className="px-3 py-2 text-center">Correct</th>
            <th className="px-3 py-2 text-center">Rank Pts</th>
            <th className="px-3 py-2 text-center">Bonus</th>
            <th className="px-3 py-2 text-center">Final Total</th>
          </tr>
        </thead>
        <tbody>
          {data.weekly.map((r) => (
            <tr key={r.playerId} className="border-b border-line last:border-0">
              <td className="px-3 py-2 text-center font-display text-lg text-grove">
                {ordinal(r.placement)}
              </td>
              <td className="px-3 py-2 font-medium">{r.name}</td>
              <td className="px-3 py-2 text-center">
                {r.correctPicks}/{data.totalGames}
              </td>
              <td className="px-3 py-2 text-center">{r.leaguePoints}</td>
              <td className="px-3 py-2 text-center text-grove">
                {r.bonusPoints ? `+${r.bonusPoints}` : '—'}
              </td>
              <td className="px-3 py-2 text-center font-bold">{r.finalTotal}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OverallTable({ data }: { data: AdminWeekStandingsDTO }) {
  return (
    <div className="overflow-x-auto">
      <h3 className="mb-2 font-display text-2xl uppercase text-ink">
        {data.league.name} — {data.season.name} overall standings
      </h3>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-line bg-cream/60 text-xs uppercase tracking-wide text-muted">
            <th className="px-3 py-2 text-center">Rank</th>
            <th className="px-3 py-2 text-left">Player</th>
            <th className="px-3 py-2 text-center">Total</th>
            <th className="px-3 py-2 text-center">Bonus</th>
            <th className="px-3 py-2 text-center">Correct</th>
            <th className="px-3 py-2 text-center">Weeks</th>
          </tr>
        </thead>
        <tbody>
          {data.overall.map((r) => (
            <tr key={r.playerId} className="border-b border-line last:border-0">
              <td className="px-3 py-2 text-center font-display text-lg text-grove">
                {ordinal(r.rank)}
              </td>
              <td className="px-3 py-2 font-medium">{r.name}</td>
              <td className="px-3 py-2 text-center font-bold">{r.totalPoints}</td>
              <td className="px-3 py-2 text-center text-grove">
                {r.totalBonusPoints ? `+${r.totalBonusPoints}` : '—'}
              </td>
              <td className="px-3 py-2 text-center">{r.totalCorrectPicks}</td>
              <td className="px-3 py-2 text-center">{r.weeksPlayed}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Fetches the brand logo as a data URI so it embeds (and prints) reliably.
 * Uses the DARK logo (favicon-96x96.png) — logo.png / favicon.svg are the
 * white variant and would be invisible on white paper.
 */
async function logoDataUri(): Promise<string> {
  try {
    const res = await fetch('/brand/favicon-96x96.png');
    if (!res.ok) return '';
    const blob = await res.blob();
    return await new Promise<string>((resolve) => {
      const fr = new FileReader();
      fr.onloadend = () => resolve(typeof fr.result === 'string' ? fr.result : '');
      fr.onerror = () => resolve('');
      fr.readAsDataURL(blob);
    });
  } catch {
    return '';
  }
}

/**
 * Opens a branded, portrait, print-ready window with BOTH standings tables
 * (weekly, then a guaranteed page break, then overall) and triggers the
 * browser's print dialog.
 */
async function printStandings(data: AdminWeekStandingsDTO) {
  const logo = await logoDataUri();
  const w = window.open('', '_blank', 'width=800,height=1000');
  if (!w) return;

  const weeklyRows = data.weekly
    .map(
      (r) => `<tr>
        <td class="c place">${ordinal(r.placement)}${
          r.placement === 1
            ? '<span class="prize">$25 gift card</span>'
            : r.placement === 2
              ? '<span class="prize">$10 gift card</span>'
              : ''
        }</td>
        <td class="name">${escapeHtml(r.name)}</td>
        <td class="c">${r.correctPicks}/${data.totalGames}</td>
        <td class="c">${r.leaguePoints}</td>
        <td class="c">${r.bonusPoints ? `+${r.bonusPoints}` : '—'}</td>
        <td class="c total">${r.finalTotal}</td>
      </tr>`,
    )
    .join('');

  const overallRows = data.overall
    .map(
      (r) => `<tr>
        <td class="c place">${ordinal(r.rank)}</td>
        <td class="name">${escapeHtml(r.name)}</td>
        <td class="c total">${r.totalPoints}</td>
        <td class="c">${r.totalBonusPoints ? `+${r.totalBonusPoints}` : '—'}</td>
        <td class="c">${r.totalCorrectPicks}</td>
        <td class="c">${r.weeksPlayed}</td>
      </tr>`,
    )
    .join('');

  const logoImg = logo ? `<img class="logo" src="${logo}" alt="Lost Grove" />` : '';
  const sheet = (
    subtitle: string,
    title: string,
    headCells: string,
    bodyRows: string,
    breakBefore: boolean,
  ) => `
    <section class="sheet${breakBefore ? ' break' : ''}">
      <header>
        ${logoImg}
        <h1>Lost Grove Pick 'Em</h1>
        <p class="sub">${escapeHtml(subtitle)}</p>
        <p class="title">${escapeHtml(title)}</p>
      </header>
      <table>
        <thead><tr>${headCells}</tr></thead>
        <tbody>${bodyRows}</tbody>
      </table>
      <footer>Lost Grove Brewing · lostpickems.com</footer>
    </section>`;

  const weeklyHead = `
    <th class="c">Place</th><th class="l">Player</th><th class="c">Correct</th>
    <th class="c">Rank Pts</th><th class="c">Bonus</th><th class="c">Final Total</th>`;
  const overallHead = `
    <th class="c">Rank</th><th class="l">Player</th><th class="c">Total</th>
    <th class="c">Bonus</th><th class="c">Correct</th><th class="c">Weeks</th>`;

  w.document.write(`<!doctype html><html><head><meta charset="utf-8">
    <title>Standings — ${escapeHtml(data.league.name)} · ${escapeHtml(data.week.displayName)}</title>
    <style>
      @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Roboto:wght@400;500;700&display=swap');
      @page { size: portrait; margin: 0.6in; }
      * { box-sizing: border-box; }
      body { margin: 0; font-family: Roboto, sans-serif; color: #161616; }
      .sheet { padding: 0 0 24px; }
      /* Guaranteed page break before the second (overall) sheet. */
      .sheet.break { break-before: page; page-break-before: always; }
      header { text-align: center; border-bottom: 4px solid #E1AB47;
               padding-bottom: 12px; margin-bottom: 18px; }
      .logo { display: block; margin: 0 auto 8px; height: 72px; width: auto; }
      h1 { font-family: 'Bebas Neue', sans-serif; font-size: 40px; letter-spacing: 1px;
           color: #3C462C; margin: 0; }
      .sub { font-size: 15px; color: #6B6B6B; margin: 2px 0 0; }
      .title { font-family: 'Bebas Neue', sans-serif; font-size: 26px; letter-spacing: .5px;
               color: #161616; margin: 10px 0 0; text-transform: uppercase; }
      table { width: 100%; border-collapse: collapse; font-size: 14px; }
      thead th { background: #3C462C; color: #fff; font-weight: 700; text-align: center;
                 padding: 9px 10px; font-size: 12px; text-transform: uppercase; letter-spacing: .4px; }
      thead th.l { text-align: left; }
      tbody td { padding: 9px 10px; border-bottom: 1px solid #E4DFD3; }
      tbody tr:nth-child(even) td { background: #F6F3EC; }
      td.c { text-align: center; }
      td.name { font-weight: 500; }
      td.place { font-family: 'Bebas Neue', sans-serif; font-size: 20px; color: #3C462C; }
      td.place .prize { display: block; font-family: Roboto, sans-serif; font-size: 9px;
                        font-weight: 700; letter-spacing: .3px; text-transform: uppercase;
                        color: #E1AB47; margin-top: 1px; }
      td.total { font-weight: 700; }
      footer { margin-top: 14px; font-size: 11px; color: #6B6B6B; text-align: center; }
    </style></head><body>
    ${sheet(
      `${data.league.name} — ${data.week.displayName}`,
      'Weekly Standings',
      weeklyHead,
      weeklyRows || `<tr><td colspan="6" class="c">No results.</td></tr>`,
      false,
    )}
    ${sheet(
      `${data.league.name} — ${data.season.name}`,
      'Overall Standings',
      overallHead,
      overallRows || `<tr><td colspan="6" class="c">No results.</td></tr>`,
      true,
    )}
    <script>
      (function () {
        var printed = false;
        function go() { if (printed) return; printed = true; window.focus(); window.print(); }
        window.onafterprint = function () { window.close(); };
        // Fire once everything (fonts + logo) has loaded; hard fallback in case load already passed.
        window.addEventListener('load', function () { setTimeout(go, 300); });
        setTimeout(go, 1500);
      })();
    </script>
    </body></html>`);
  w.document.close();
  w.focus();
}
